import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import Dropdown, {
  DropdownItemProps,
  DropdownOption,
  DropdownOptionGroup,
} from 'src/components/Dropdown/Dropdown'
import { ExtendCurrency } from 'src/enums'
import { translateSingleString } from 'src/localization/Trns'
import { sessionConfigQueries } from 'src/queries/session/config/config.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css, CSSProp } from 'styled-components'
import worldCurrencies from 'world-currencies'

export interface CurrencyOptionDetails {
  currency: worldCurrencies.Currency
  displayName: string
  smallDisplay: boolean
}

export type CurrencyOption = DropdownOption<CurrencyOptionDetails>

const CurrencyDropdownItem: React.FC<
  DropdownItemProps<CurrencyOptionDetails>
> = (props): React.ReactElement => {
  return (
    <div
      className={props.className}
      css={css`
        display: flex;
        align-items: center;
        flex-grow: 2;
        height: ${props.isRoot ? '100%' : '60px'};
      `}
    >
      {props.details !== undefined && (
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                props.isRoot ? 14 : 16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
              ${props.isSelected &&
              css`
                ${getFontCss(
                  props.isRoot ? 14 : 16,
                  themeConstants.colors.cta,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            `}
          >
            {props.isRoot &&
              props.details.smallDisplay &&
              props.details.currency.iso.code}
            {!(props.isRoot && props.details.smallDisplay) &&
              props.details.displayName}
          </span>
        </div>
      )}
    </div>
  )
}

const CurrencyOptionFilter = (option: CurrencyOption, val: string): boolean =>
  !!option.details?.displayName.toLowerCase().includes(val.toLowerCase())

const getCurrencyOptionFilterText = (option: CurrencyOption): string =>
  (!option.details?.smallDisplay && option.details?.displayName) ||
  option.value ||
  ''

interface CurrencyDropdownProps {
  className?: string
  selectedValue?: string
  chooseOption: (val: ExtendCurrency) => void
  errors?: Array<React.ReactElement | string>
  smallDisplay?: boolean
  rootContainerCss?: CSSProp
}

const CurrencyDropdown: React.FC<CurrencyDropdownProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  // Default [] preserves the legacy redux initial-state render (empty
  // dropdown while pending) — the config reducer had no loading/error flags.
  const { data: currencies = [] } = useQuery({
    ...sessionConfigQueries.byLocale(locale),
    select: (config) => config.supportedCurrencies,
  })
  const optionGroups: Array<DropdownOptionGroup<CurrencyOption>> = useMemo(
    () => [
      {
        options: currencies.map((curr) => {
          const currencyInfo = worldCurrencies[curr]
          return {
            value: currencyInfo.iso.code,
            details: {
              currency: currencyInfo,
              displayName: translateSingleString(
                locale,
                currencyInfo.name + ' (${isoCode})',
                { isoCode: currencyInfo.iso.code },
              ),
              smallDisplay:
                props.smallDisplay === undefined ? false : props.smallDisplay,
            },
          }
        }),
      },
    ],
    [currencies],
  )

  return (
    <Dropdown
      className={props.className}
      DropdownItem={CurrencyDropdownItem}
      optionGroups={optionGroups}
      isMulti={false}
      selectedValues={
        props.selectedValue !== undefined
          ? new Set([props.selectedValue])
          : new Set([])
      }
      chooseOption={(val: string): void => {
        props.chooseOption(val as ExtendCurrency)
      }}
      errors={props.errors}
      filter={CurrencyOptionFilter}
      getFilterText={getCurrencyOptionFilterText}
      validateNewOption={undefined}
      smallDisplay={
        props.smallDisplay === undefined ? false : props.smallDisplay
      }
      rootContainerCss={props.rootContainerCss}
    />
  )
}

export default CurrencyDropdown
